import { useState } from 'react'
import { Search, ChevronLeft, ChevronRight, Radio, Video, Tag, Film, Archive, Trash2, X, MessageSquare, Link, CheckCircle, AlertCircle, Clock, RefreshCw } from 'lucide-react'

function isLiveRecording(video) {
  return !!(video?.liveInput || video?.input?.uid || video?.live_input)
}

export default function VideoList({
  videos,
  selected,
  loading,
  paging,
  onSelect,
  onSearch,
  onPageChange,
  onPerPageChange,
  allTags,
  activeTag,
  onTagFilter,
  tagFilterUIDs,
  checked,
  onToggle,
  onToggleAll,
  onClearSelection,
  onBulkTag,
  onBulkRequestDownloads,
  onBulkArchive,
  onBulkDelete,
  onBulkCaptions,
  onBulkVerify,
  onBulkRetryArchive,
  bulkBusy,
  vodLinks,
  archiveStatuses = {},
}) {
  const [searchTerm, setSearchTerm] = useState(paging.search)

  function handleSearchSubmit(e) {
    e.preventDefault()
    onSearch(searchTerm)
  }

  // Apply tag filter client-side
  const displayVideos = tagFilterUIDs !== null
    ? videos.filter(v => tagFilterUIDs.includes(v.uid))
    : videos

  const allChecked = displayVideos.length > 0 && displayVideos.every(v => checked.has(v.uid))
  const someChecked = checked.size > 0

  return (
    <>
      <div className="video-list-controls">
        <form className="video-list-search" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search videos..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <button type="submit">
            <Search size={16} />
          </button>
        </form>

        <div className="video-list-tag-filter">
          <Tag size={13} />
          <select
            value={activeTag}
            onChange={e => onTagFilter(e.target.value)}
          >
            <option value="">All Tags</option>
            {(allTags || []).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        
        <div className="video-list-pagination">
          <label>
            Per page:
            <select 
              value={paging.perPage} 
              onChange={e => onPerPageChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          
          <button 
            onClick={() => onPageChange(paging.page - 1)}
            disabled={paging.page <= 1}
          >
            <ChevronLeft size={14} />
          </button>
          <span>Page {paging.page}</span>
          <button onClick={() => onPageChange(paging.page + 1)}>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {someChecked && (
        <div className="bulk-action-bar">
          <span className="bulk-count">{checked.size} selected</span>
          <button onClick={onBulkTag} disabled={bulkBusy}><Tag size={12} /> Tag</button>
          <button onClick={onBulkCaptions} disabled={bulkBusy}><MessageSquare size={12} /> Captions</button>
          <button onClick={onBulkRequestDownloads} disabled={bulkBusy}><Film size={12} /> Request Downloads</button>
          <button onClick={onBulkArchive} disabled={bulkBusy}><Archive size={12} /> Archive</button>
          <button onClick={onBulkVerify} disabled={bulkBusy}><CheckCircle size={12} /> Verify</button>
          <button onClick={onBulkRetryArchive} disabled={bulkBusy}><RefreshCw size={12} /> Retry</button>
          <button onClick={onBulkDelete} disabled={bulkBusy} className="danger"><Trash2 size={12} /> Delete</button>
          <button onClick={onClearSelection} className="bulk-clear"><X size={12} /> Clear</button>
        </div>
      )}

      <div className="video-list-table">
        <table>
          <thead>
            <tr>
              <th className="bulk-checkbox-cell">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={() => onToggleAll(displayVideos)}
                />
              </th>
              <th>Title</th>
              <th>Type</th>
              <th>Archive</th>
              <th>VOD</th>
              <th>UID</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 20 }}>
                  Loading...
                </td>
              </tr>
            ) : displayVideos.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 20 }}>
                  {activeTag ? `No videos tagged "${activeTag}"` : 'No videos found'}
                </td>
              </tr>
            ) : (
              displayVideos.map(video => {
                const vod = vodLinks?.[video.uid]
                return (
                  <tr
                    key={video.uid}
                    className={selected?.uid === video.uid ? 'selected' : ''}
                  >
                    <td className="bulk-checkbox-cell" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={checked.has(video.uid)}
                        onChange={() => onToggle(video.uid)}
                      />
                    </td>
                    <td className="video-title-cell" onClick={() => onSelect(video.uid)}>
                      {video.meta?.name || '(untitled)'}
                    </td>
                    <td className="video-type-cell" onClick={() => onSelect(video.uid)}>
                      {isLiveRecording(video) ? (
                        <span className="badge-live"><Radio size={10} /> LIVE</span>
                      ) : (
                        <span className="badge-upload"><Video size={10} /> VOD</span>
                      )}
                    </td>
                    <td className="video-archive-cell" onClick={() => onSelect(video.uid)}>
                      {(() => {
                        const info = archiveStatuses[video.uid]
                        if (!info || info.status === 'none') return <span className="archive-badge archive-none">—</span>
                        if (info.status === 'done') return <span className="archive-badge archive-done"><Archive size={10} /></span>
                        if (info.status === 'error' || info.status === 'not_found') return <span className="archive-badge archive-error"><AlertCircle size={10} /></span>
                        return <span className="archive-badge archive-progress"><Clock size={10} /></span>
                      })()}
                    </td>
                    <td className="video-vod-cell" onClick={() => onSelect(video.uid)}>
                      {vod?.linked ? (
                        <span className="vod-linked-badge"><Link size={10} /> Linked</span>
                      ) : (
                        <span className="vod-unlinked">—</span>
                      )}
                    </td>
                    <td className="video-uid-cell" onClick={() => onSelect(video.uid)}>
                      {video.uid.substring(0, 8)}...
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}
